import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Repeat, ShieldCheck, User as UserIcon, Users as SupervisorIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

// Which roles share one login session on one phone+tenant, and where each
// one lands. Keep in sync with backend routes_auth.py's SWITCHABLE_ROLES.
const ROLE_META = {
  customer: { label: "Customer", home: "/app/home", Icon: UserIcon },
  tenant_admin: { label: "Admin", home: "/admin/dashboard", Icon: ShieldCheck },
  supervisor: { label: "Supervisor", home: "/admin/dashboard", Icon: SupervisorIcon },
};
const SWITCHABLE_ROLES = Object.keys(ROLE_META);

/**
 * "Switch User" — lets a phone that holds more than one role on the same
 * tenant (e.g. Customer + Admin, or Customer + Supervisor, or all three)
 * hop between them from inside ONE app session, no separate login and no
 * separate mobile app per role. Only renders when GET /api/auth/roles
 * actually returns more than the current role.
 *
 * Reuses the existing auth flow end to end: POST /api/auth/switch-role
 * re-issues a JWT for the target role without a fresh OTP, then the result
 * is fed straight into the same login() the OTP-verify screen uses — so
 * switching into Admin/Supervisor from the customer app (including the
 * native mobile build, which is just this same SPA in a WebView) needs no
 * native code changes: it's plain in-app navigation to /admin/dashboard
 * once the session token is for that role.
 */
export default function RoleSwitcher({ compact = false }) {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!user || !SWITCHABLE_ROLES.includes(user.role)) return;
    api.get("/api/auth/roles").then(({ data }) => setRoles(data.roles || [])).catch(() => {});
  }, [user]);

  const others = roles.filter((r) => r !== user?.role && ROLE_META[r]);
  if (others.length === 0) return null;

  const switchTo = async (role) => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/switch-role", { role });
      login(data.token, data.user, data.tenant || null, data.refresh_token || null);
      toast.success(`Switched to ${ROLE_META[role].label} view`);
      setPickerOpen(false);
      nav(ROLE_META[role].home);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch view");
    } finally {
      setBusy(false);
    }
  };

  const btnClass = compact
    ? "w-full flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm font-bold text-brand-sky hover:bg-brand-sky/10 disabled:opacity-50"
    : "w-full bg-white rounded-3xl border border-neutral-200 p-5 flex items-center gap-3 font-bold text-brand-sky disabled:opacity-50";
  const iconCls = compact ? "w-4 h-4" : "w-5 h-5";

  // Exactly one alternate role — single-tap switch, same UX as before this
  // component supported a 3rd role.
  if (others.length === 1) {
    const other = others[0];
    return (
      <button onClick={() => switchTo(other)} disabled={busy} className={btnClass} data-testid="role-switch-btn">
        <Repeat className={iconCls} /> {busy ? "Switching…" : `Switch to ${ROLE_META[other].label} View`}
      </button>
    );
  }

  // Two or more alternates (e.g. Customer + Admin + Supervisor on the same
  // phone) — a picker instead of guessing which one to jump to.
  return (
    <>
      <button onClick={() => setPickerOpen(true)} disabled={busy} className={btnClass} data-testid="role-switch-btn">
        <Repeat className={iconCls} /> Switch User
      </button>
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="role-switch-menu">
          <DialogHeader><DialogTitle className="font-heading text-xl">Switch User</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {others.map((r) => {
              const { label, Icon } = ROLE_META[r];
              return (
                <button
                  key={r}
                  onClick={() => switchTo(r)}
                  disabled={busy}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border border-neutral-200 hover:bg-brand-sky/5 hover:border-brand-sky/40 font-bold text-left disabled:opacity-50"
                  data-testid={`role-switch-${r}`}
                >
                  <Icon className="w-5 h-5 text-brand-sky shrink-0" />
                  {busy ? "Switching…" : `Continue as ${label}`}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
